import { useEffect, useState } from "react";
import { User } from "@/types/user";
import { Education } from "@/types/jobPost";
import { UpdateUserEducationData } from "../mutations/updateUserEducation";

export const useUpdateEducationManager = (user: User | null) => {
  const [userEducations, setUserEducations] = useState<User["user_educations"]>(
    [],
  );
  const [selectedEducations, setSelectedEducations] = useState<Education[]>([]);
  const [educationDataObject, setEducationDataObject] = useState<
    UpdateUserEducationData[]
  >([]);

  useEffect(() => {
    if (user) {
      setUserEducations(user.user_educations || []);
      setSelectedEducations(user.user_educations.map(ue => ue.education));
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const selectedIds = selectedEducations.map(e => e.id);
    const originalIds = user.user_educations.map(ue => ue.education.id);

    const additions: UpdateUserEducationData[] = selectedIds
      .filter(id => !originalIds.includes(id))
      .map(id => ({
        education_id: id,
        institution_name: "", // Must be filled by frontend UI for each new selection
        is_currently_studying: false,
        remove_education: false,
      }));

    const removals: UpdateUserEducationData[] = originalIds
      .filter(id => !selectedIds.includes(id))
      .map(id => ({
        education_id: id,
        remove_education: true,
        institution_name: "",
      }));

    const retained: UpdateUserEducationData[] = user.user_educations
      .filter(ue => selectedIds.includes(ue.education.id))
      .map(ue => ({
        education_id: ue.education.id,
        institution_name: ue.institution_name,
        is_currently_studying: ue.is_currently_studying,
        remove_education: false,
      }));

    setEducationDataObject([...retained, ...additions, ...removals]);
  }, [selectedEducations, user]);

  const educationsHaveChanged = () => {
    const original = userEducations.map(ue => ue.education.id).sort();
    const current = selectedEducations.map(e => e.id).sort();
    return (
      original.length !== current.length ||
      original.some((id, i) => id !== current[i])
    );
  };

  const resetEducations = () => {
    if (!user) return;
    setUserEducations(user.user_educations || []);
    setSelectedEducations(user.user_educations.map(ue => ue.education));
    setEducationDataObject([]);
  };

  return {
    userEducations,
    selectedEducations,
    setSelectedEducations,
    educationDataObject,
    educationsHaveChanged,
    resetEducations,
  };
};
